import React from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';
import DataGrid from '../../../shared/datagrid/DataGrid.tsx';
import Message from '../../../shared/message/Message.js';
import TextButton from '../../../shared/buttons/TextButton.tsx';
import StatusDocumentoInscricao from '../../components/inscricao/StatusDocumentoInscricao.jsx';
import {
    formatarTamanho,
    ouTraco,
    situacaoAvaliacao,
    situacaoDocumento
} from '../../components/inscricao/documentoInscricao.js';
import {subtitle} from '../../../../utils/styles.js';

const ID_TITULO = 'dialog-detalhe-documento-inscricao-titulo';

const useStyles = makeStyles(theme => ({
    root: {
        width: 900,
        maxWidth: '100%'
    },
    contentRoot: {
        overflowY: 'initial'
    },
    ...subtitle(theme),
    bloco: {
        marginTop: theme.spacing(1)
    }
}));

/*
 * Detalhes de um documento da inscrição, só leitura: documento, arquivo e
 * avaliação (com o histórico quando houver mais de uma).
 */
export default function DialogDetalheDocumentoInscricao({open, onClose, anexo, deQuem}) {
    const classes = useStyles();

    const situacao = situacaoDocumento(anexo);
    const naoSalvo = situacao === 'NAO_SALVO';
    const arquivo = anexo?.anexo || {};
    const avaliacao = anexo?.avaliacao || null;
    // Do mais recente ao mais antigo; ativa:false é avaliação desfeita. Sem avaliação ativa, a `avaliacao` vem ausente.
    const historico = anexo?.historicoAvaliacoes || [];

    const item = (rotulo, valor, sm = 4) => (
        <Grid item xs={12} sm={sm}>
            <LabelValue label={rotulo} value={ouTraco(valor)}/>
        </Grid>
    );

    const titulo = texto => (
        <Grid item xs={12} className={classes.bloco}>
            <Typography variant='subtitle1' className={classes.subtitle}>{texto}</Typography>
        </Grid>
    );

    return (
        <Dialog open={open} onClose={onClose} scroll='body' aria-labelledby={ID_TITULO}
                classes={{paper: classes.root}} maxWidth={false}>
            <DialogTitle id={ID_TITULO}>Detalhes do documento</DialogTitle>
            <DialogContent classes={{root: classes.contentRoot}}>
                {anexo && (
                    <Grid container spacing={3}>
                        {titulo('Documento')}
                        {item('Tipo do documento', anexo.documento?.nome)}
                        {item('Descrição', anexo.descricao || anexo.documento?.descricao)}
                        {item('De quem', deQuem)}

                        {titulo('Arquivo')}
                        {item('Nome', arquivo.nome)}
                        {item('Tipo', arquivo.mimetype)}
                        {item('Tamanho', formatarTamanho(arquivo.tamanho))}
                        {item('Enviado em', naoSalvo ? 'Ainda não salvo' : anexo.dataEnvio)}

                        {titulo('Avaliação')}
                        {naoSalvo || !avaliacao ? (
                            <Grid item xs={12}>
                                <Message type='info' variant='standard'>
                                    {naoSalvo && anexo.id
                                        ? 'O arquivo foi trocado e ainda não foi salvo: a nova versão ainda não foi avaliada.'
                                        : historico.length
                                            ? 'Este documento não tem avaliação vigente: a avaliação anterior foi desfeita.'
                                            : 'Este documento ainda não foi avaliado.'}
                                </Message>
                            </Grid>
                        ) : (
                            <>
                                <Grid item xs={12} sm={4}>
                                    <LabelValue label='Situação'>
                                        <div>
                                            <StatusDocumentoInscricao situacao={situacao}/>
                                        </div>
                                    </LabelValue>
                                </Grid>
                                {item('Data da avaliação', avaliacao.data)}
                                {item('Avaliado por', avaliacao.usuario)}
                            </>
                        )}
                        {!naoSalvo && (historico.length > 1 || (!avaliacao && historico.length > 0)) && (
                            <Grid item xs={12}>
                                <LabelValue label='Histórico de avaliações'>
                                    <DataGrid
                                        rows={historico}
                                        tableContainerProps={{component: 'div'}}
                                        noRecordsText='Nenhuma avaliação.'
                                        columns={[
                                            {
                                                label: 'Situação', field: 'status', sortable: false,
                                                valueGetter: valor => <StatusDocumentoInscricao situacao={situacaoAvaliacao(valor)}/>
                                            },
                                            {label: 'Data', field: 'data', valueGetter: valor => ouTraco(valor)},
                                            {label: 'Avaliador', field: 'usuario', valueGetter: valor => ouTraco(valor)},
                                            {label: 'Vigência', field: 'ativa', valueGetter: valor => (valor ? 'Ativa' : 'Inativa')}
                                        ]}/>
                                </LabelValue>
                            </Grid>
                        )}
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <TextButton onClick={onClose}>Fechar</TextButton>
            </DialogActions>
        </Dialog>
    );
}

DialogDetalheDocumentoInscricao.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    anexo: PropTypes.object,
    deQuem: PropTypes.string
};
